/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's copyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

import { getAuthToken, verifyToken, isTokenNearExpiry, getValidAuthToken } from "@services/auth/auth";

/**
 * Checks if user is already authenticated.
 * 
 * @param {any} auth - Auth context
 * @returns {boolean} True if authenticated
 */
export function isUserAuthenticated(auth: any): boolean {
  return auth.isAuthenticated === true;
}

/**
 * Attempts a token refresh when the current token is at or past expiry.
 *
 * @param token - The current (possibly expired) auth token.
 * @returns `true` if a distinct fresh token was obtained and verified.
 */
async function tryRefreshToken(token: string): Promise<boolean> {
  if (!isTokenNearExpiry(0)) return false;
  const freshToken = await getValidAuthToken();
  if (!freshToken || freshToken === token) return false;
  return verifyToken(freshToken);
}

/**
 * Verifies authentication token, attempting refresh if expired.
 * 
 * @returns {Promise<boolean>} True if token is valid
 */
export async function verifyAuthToken(): Promise<boolean> {
  const token = getAuthToken();
  if (!token) return false;
  if (await verifyToken(token)) return true;
  return tryRefreshToken(token);
}

/**
 * Determines authentication status asynchronously.
 * 
 * @param {any} auth - Auth context
 * @returns {Promise<boolean>} True if authenticated
 */
export async function determineAuthStatusAsync(auth: any): Promise<boolean> {
  if (isUserAuthenticated(auth)) {
    return true;
  }
  return verifyAuthToken();
}
